// src/services/firebaseAuth.js — the member's Firestore identity, kept in step
// with their Clerk login. adr-firestore-identity.md step a2.
//
// INVISIBLE BY DESIGN (owner decision 2026-10-05): nothing here ever holds up
// rendering. The hand-over runs in the background; only a Firestore WRITE that
// is attempted before it finishes waits for it (see firestoreWrite.js), capped
// at WRITE_WAIT_CAP_MS. Optimistic UI means even that wait is never on screen.
//
// Lifecycle
//   • Clerk not loaded yet        → writes wait (rare: protected UI renders
//                                    only after Clerk loads), capped.
//   • Signed out                  → ready immediately; any Firebase session for
//                                    a previous user is signed out.
//   • Signed in, Firebase already → ready immediately (restored from IndexedDB:
//     this uid + role                the normal case, no network).
//   • Signed in, needs a token    → one POST /api/firebase-token, then
//                                    signInWithCustomToken. Once per device.
//
// Nothing is ENFORCED by a2: the rules still allow everything, so a failure
// here can't block a member. Failures are reported to Sentry
// (`firebase-auth-missing`) because a3 may only ship after 7 clean days.
//
// No import from ./firebase on purpose: firebase.js imports firestoreWrite.js,
// which imports this file — getApp() avoids the cycle.

import { getApp } from 'firebase/app';
import * as Sentry from '@sentry/react';

export const WRITE_WAIT_CAP_MS = 4000;

let authModule = null;        // lazily-loaded 'firebase/auth' (kept out of the first paint)
let auth = null;
let enabled = true;           // siteConfig.firebaseAuthEnabled !== false

// What the app wants: undefined = Clerk not loaded yet, null = signed out,
// { userId, role, getToken } = signed in.
let desired;
let currentUid = null;        // Firebase's current uid (mirrors onAuthStateChanged)
let currentRole = null;
let syncSeq = 0;              // guards against overlapping syncs on fast user switches
let reportedMissing = false;

// One shared "ready" promise. It is only ever REPLACED after it has resolved,
// so a write already waiting on it is never orphaned onto a promise that will
// not settle (it would otherwise sit out the full cap).
let readyResolve = null;
let readyPromise = null;
let readySettled = true;
function armReady() {
    if (!readySettled) return; // a waiter-held promise is still pending — keep it
    readySettled = false;
    readyPromise = new Promise((r) => { readyResolve = () => { readySettled = true; r(); }; });
}
armReady();

function markReadyIfSatisfied() {
    if (isReadyNow() && !readySettled) readyResolve();
}

export function isReadyNow() {
    if (!enabled) return true;
    if (desired === undefined) return false;
    if (desired === null) return true;
    return currentUid === desired.userId && currentRole === desired.role;
}

/**
 * Resolves when writes may proceed: identity matches the Clerk user (or there
 * is no Clerk user), or after `capMs` regardless — never rejects.
 */
export function firebaseAuthReady(capMs = WRITE_WAIT_CAP_MS) {
    if (isReadyNow()) return Promise.resolve();
    let timer;
    const cap = new Promise((r) => { timer = setTimeout(() => { reportMissing('write-wait-cap'); r(); }, capMs); });
    return Promise.race([readyPromise, cap]).finally(() => clearTimeout(timer));
}

function reportMissing(reason, extra = {}) {
    // Only signed-in members matter for the a3 gate; once per page load.
    if (reportedMissing || !desired) return;
    reportedMissing = true;
    try {
        Sentry.captureMessage('firebase-auth-missing', {
            level: 'warning',
            tags: { firebaseAuth: reason },
            extra: { reason, ...extra },
        });
    } catch (_) { /* telemetry must never throw */ }
}

async function loadAuth() {
    if (auth) return auth;
    authModule = await import('firebase/auth');
    const app = getApp();
    try {
        // initializeAuth (not getAuth) with IndexedDB only: no popup/redirect
        // resolver needed for custom tokens, so the lazy chunk stays small.
        auth = authModule.initializeAuth(app, { persistence: authModule.indexedDBLocalPersistence });
    } catch (_) {
        auth = authModule.getAuth(app); // already initialized (hot reload / StrictMode)
    }
    authModule.onAuthStateChanged(auth, (user) => {
        currentUid = user ? user.uid : null;
        if (!user) currentRole = null;
    });
    return auth;
}

async function roleOf(user) {
    try {
        const r = await user.getIdTokenResult(); // cached locally unless expired
        return r.claims.role || null;
    } catch (_) { return null; }
}

async function sync() {
    const seq = ++syncSeq;
    const want = desired;
    try {
        const a = await loadAuth();
        await a.authStateReady(); // IndexedDB restore finished
        if (seq !== syncSeq) return;

        if (!want) {
            if (a.currentUser) await authModule.signOut(a);
            currentUid = null; currentRole = null;
            markReadyIfSatisfied();
            return;
        }

        if (a.currentUser && a.currentUser.uid === want.userId) {
            const role = await roleOf(a.currentUser);
            if (seq !== syncSeq) return;
            if (role === want.role) {
                currentUid = want.userId; currentRole = role;
                markReadyIfSatisfied();
                return;
            }
        }

        // Need a fresh identity (first time on this device, a different user,
        // or a role change such as a friend grant).
        const clerkJwt = await want.getToken();
        if (!clerkJwt) { reportMissing('no-clerk-token'); return; }
        const resp = await fetch('/api/firebase-token', {
            method: 'POST',
            headers: { Authorization: `Bearer ${clerkJwt}` },
        });
        if (!resp.ok) { reportMissing('mint-http-' + resp.status); return; }
        const { token } = await resp.json();
        if (seq !== syncSeq) return;
        const cred = await authModule.signInWithCustomToken(a, token);
        if (seq !== syncSeq) return;
        currentUid = cred.user.uid;
        currentRole = await roleOf(cred.user);
        if (currentUid !== want.userId) { reportMissing('uid-mismatch'); return; }
        markReadyIfSatisfied();
    } catch (e) {
        reportMissing('sync-error', { code: e?.code || null, message: String(e?.message || e).slice(0, 200) });
    }
}

/**
 * Called by <FirebaseIdentitySync/> whenever Clerk's state changes.
 * @param {null|{userId:string, role:string, getToken:()=>Promise<string>}} identity
 *        null = signed out. role must be 'admin' | 'friend' | 'member'.
 * @param {{enabled?: boolean}} opts  siteConfig.firebaseAuthEnabled kill switch.
 */
export function setClerkIdentity(identity, opts = {}) {
    enabled = opts.enabled !== false;
    const sameAsBefore = desired !== undefined
        && ((identity === null && desired === null)
            || (identity && desired && identity.userId === desired.userId && identity.role === desired.role));
    if (identity && desired && sameAsBefore) {
        desired = { ...identity }; // refresh getToken closure only
        return;
    }
    if (sameAsBefore) return;
    desired = identity ? { ...identity } : null;
    if (!isReadyNow()) armReady();
    if (!enabled) { if (!readySettled) readyResolve(); return; }
    sync();
    markReadyIfSatisfied();
}
